import { templateToTitle } from '@apps/card/Card.utils';
import { ThemeJson } from '@apps/card/routes/CardCustomizer';
import { Page } from '@apps/card/routes/CardCustomizer/components/Page';
import { fillPageWithEventInfo } from '@apps/card/utils/fillPageWithEventInfo';
import { EventType, StationeryTemplateCategoryEnum, useServiceCenterStationeryTemplatesContainerQuery } from '@graphql/generated';
import { EventInfoData } from '@shared/utils/eventInfo';
import { EventVenue } from '@shared/utils/eventPlace';
import useDebounce from '@shared/utils/hooks/useDebounce';
import { Flex, styled, TextV2 } from '@withjoy/joykit';
import { cloneDeep } from 'lodash-es';
import React, { useEffect, useState } from 'react';

const Container = styled.div`
  padding: 2rem;
  padding-bottom: 4rem;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  color: white;
  background-color: hsl(0, 0%, 25%);
  min-height: 100vh;
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const TextArea = styled.textarea`
  resize: none;
  width: 100%;
  height: 120px;
  font-size: 0.9rem;
  padding: 1rem;
  line-height: 1.5;
  color: white;
  background-color: hsla(0, 0%, 100%, 0.1);
  border: 0;

  &:focus {
    outline: none;
  }
`;

const Input = styled.input`
  border: 0;
  background-color: hsla(0, 0%, 100%, 0.1);
  color: white;
  padding: 0.5rem;
  width: 72px;

  &:focus {
    outline: none;
  }

  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
  }
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

function camelCaseToWords(word: string) {
  const result = word.replace(/([A-Z])/g, ' $1');
  return result.charAt(0).toUpperCase() + result.slice(1);
}

const MOCK_EVENT_INFO: EventInfoData = {
  eventId: '907d1082-291b-5f44-b9ae-de229bcc6f46',
  eventFirebaseId: 'e00c7f0a1828925fce57f33b3f0f3c858104e87954f8d862e',
  eventType: EventType.wedding,
  eventDisplayName: 'Jess & Jeff',
  ownerFirstName: 'Jessica',
  ownerLastName: 'Yang',
  fianceeFirstName: 'Xeff',
  fianceeLastName: 'King',
  locationInfo: {
    city: 'New York',
    country: 'United States',
    latitude: 40.7127753,
    longitude: -74.0059728,
    name: 'New York, NY, USA',
    placeId: 'ChIJOwg_06VPwokRYv534QaPC8g',
    state: 'New York'
  },
  finalizedDate: {
    milliseconds: 1790146800000,
    dateString: 'September 23, 2026'
  }
};

const MOCK_VENUE_INFO: EventVenue = {
  id: 'e24e72c7-489b-4832-a1b8-d6394555e304',
  googlePlaceId: 'ChIJ3aqq5Q1ZwokRb9hLO7Gyxgw',
  name: 'Edge',
  displayName: 'Edge, 30 Hudson Yards, New York, NY 10001, USA',
  address: '30 Hudson Yards, New York, NY 10001, USA',
  latitude: 40.753393599999995,
  longitude: -74.00105500000001,
  hasVenue: true
};

const FILTER_PARAM_KEY = 'filter';
const FILTER_DEFAULT = [''].join('\n');

export const PrintTemplates = () => {
  const urlParams = new URLSearchParams(window.location.search);
  const [filter, setFilter] = useState(urlParams.get(FILTER_PARAM_KEY) ?? FILTER_DEFAULT);
  const [startInput, setStartInput] = useState(0);
  const start = useDebounce(startInput, 500);
  const [onlyBasic, setOnlyBasic] = useState(false);
  const [includeHoliday, setIncludeHoliday] = useState(false);
  const [includeSigns, setIncludeSigns] = useState(false);

  useEffect(() => {
    const url = new URL(window.location.href);
    const filterValue = filter.trim();
    if (filterValue == FILTER_DEFAULT) {
      url.searchParams.delete(FILTER_PARAM_KEY);
    } else {
      url.searchParams.set(FILTER_PARAM_KEY, filterValue);
    }
    history.pushState(null, '', url);
  }, [filter]);

  const { data, loading } = useServiceCenterStationeryTemplatesContainerQuery({
    variables: {
      filter: {}
    },
    batchMode: 'fast',
    fetchPolicy: 'cache-first'
  });

  if (loading) return <Container>Loading...</Container>;

  const templates = data?.stationeryTemplatesContainer.pagedResults.edges?.map(edge => edge.node) ?? [];
  const filterTerms = filter
    .split(/\n+/)
    .filter(term => term.length > 3)
    .map(term => term.toLowerCase().replaceAll(' ', '_')); // Make it easy to copy the paste the template title (instead of theme ID)
  const filteredTemplates = templates.filter(template => {
    if (template.themeId === 'test03') return false; // Invalid themeJSON payload
    if (template.themeId === 'INVALID') return false; // Invalid themeJSON payload

    const themeJSON = template.themeJSON as ThemeJson;
    const isBasic = themeJSON.card.front.layers.some(layer => layer.name === 'NAME1');
    if (isBasic !== onlyBasic) return false;

    if (template.category === StationeryTemplateCategoryEnum.sign && !includeSigns) return false;
    if (template.category === StationeryTemplateCategoryEnum.holiday && !includeHoliday) return false;

    if (!filterTerms.length) return true;
    return filterTerms.some(value => template.themeId.includes(value));
  });
  filteredTemplates.sort((a, b) => a.themeId.localeCompare(b.themeId));

  const end = Math.min(start + 100, filteredTemplates.length);

  return (
    <Container>
      <Header>
        <TextArea
          value={filter}
          onChange={e => {
            setFilter(e.target.value);
            setStartInput(0);
          }}
          spellCheck={false}
          placeholder="Bohemian Eucalyptus&#10;Doodle Accommodations&#10;etc..."
        />
        <Flex alignItems="center" gap={8}>
          <Flex alignItems="center" gap={6}>
            <Label>
              <input type="checkbox" checked={onlyBasic} onChange={e => setOnlyBasic(e.target.checked)} />
              <TextV2 typographyVariant="label2">Only Basic</TextV2>
            </Label>
            <Label>
              <input type="checkbox" checked={includeHoliday} onChange={e => setIncludeHoliday(e.target.checked)} />
              <TextV2 typographyVariant="label2">Include Holiday</TextV2>
            </Label>
            <Label>
              <input type="checkbox" checked={includeSigns} onChange={e => setIncludeSigns(e.target.checked)} />
              <TextV2 typographyVariant="label2">Include Signs</TextV2>
            </Label>
          </Flex>
          <Flex flex="1 1 auto" />
          <Flex alignItems="center" gap={3}>
            <Input type="number" value={startInput} onChange={e => setStartInput(parseInt(e.target.value))} min={0} max={filteredTemplates.length} />
            to {end} of {filteredTemplates.length} Results
          </Flex>
        </Flex>
      </Header>
      <Flex flexDirection="row" flexWrap="wrap" gap={7}>
        {filteredTemplates.slice(start, end).map(template => {
          const { themeId, category, side, widthInches, heightInches } = template;
          const page = cloneDeep(template.themeJSON.card.front);
          fillPageWithEventInfo(page, MOCK_EVENT_INFO, MOCK_VENUE_INFO);

          return (
            <Flex key={template.id} flexDirection="column" gap={4} style={{ scale: 2 }}>
              <TextV2 typographyVariant="label2" title={themeId}>
                {templateToTitle(template)}
                <br />
                {[camelCaseToWords(category), `${widthInches}×${heightInches}`, side === 'back' ? 'Back' : ''].filter(x => x).join(' ')}
                <br />
                {template.id}
              </TextV2>
              <Page page={page} width={5} height={7} />
            </Flex>
          );
        })}
      </Flex>
    </Container>
  );
};
